import { useEffect, useRef, useState } from 'react';
import SafeIcon from './SafeIcon';
import MaterialPicker from './MaterialPicker';
import PlanEditor from './PlanEditor';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { buildPlan, holdsSlotStatus } from '../store/campaigns';
import { DEMO_TODAY, type Campaign, type CampaignGoal, type PlanSlot } from '../store/seed';
import type { Channel } from '../types';
import { channelIconName, useDayFormat } from './campaignDisplay';

const goals: [CampaignGoal, string, string, string][] = [
  ['product', 'Launch a product', 'A new drink, cake or menu item.', 'FiCoffee'],
  ['season', 'Seasonal menu', 'A few weeks around a season or holiday.', 'FiSun'],
  ['event', 'Event or opening hours', 'One date to announce, remind and thank.', 'FiCalendar'],
  ['awareness', 'Get known locally', 'Introduce the place and the people.', 'FiMapPin'],
  ['always-on', 'Everyday content', 'No end date. Steady posts every week.', 'FiRepeat'],
];
const DEMO_YEAR = 2026;
const NOVEMBER_MONTH_INDEX = 10;
const MILLISECONDS_PER_DAY = 86400000;
const DAYS_IN_A_WEEK = 7;
const ALWAYS_ON_PLANNING_WINDOW_IN_DAYS = 14;
const TOMORROW = DEMO_TODAY + 1;
const MINIMUM_NAME_LENGTH = 3;
const MINIMUM_BRIEF_LENGTH = 15;
const WIZARD_STEP_COUNT = 4;
const LAST_STEP = WIZARD_STEP_COUNT;

const isoDateFromDemoDay = (demoDay: number) => {
  const date = new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, demoDay);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const demoDayFromIsoDate = (isoDate: string) => {
  const [year, month, dayOfMonth] = isoDate.split('-').map(Number);
  return Math.round(
    (new Date(year, month - 1, dayOfMonth).getTime() -
      new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, 0).getTime()) /
      MILLISECONDS_PER_DAY
  );
};

const isBusinessAccount = (account: { type: string }) => !/personal/i.test(account.type);
const languageLabel = (languageCode: 'pl' | 'en') => (languageCode === 'pl' ? 'Polish' : 'English');

function toggled<Item>(items: Item[], item: Item) {
  return items.includes(item) ? items.filter((other) => other !== item) : [...items, item];
}

function stepMarkerClass(stepNumber: number, currentStep: number) {
  if (stepNumber === currentStep) return 'current';
  if (stepNumber < currentStep) return 'done';
  return '';
}

export default function CampaignWizard({
  campaign,
  onClose,
  onSaved,
}: {
  campaign?: Campaign;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { translate, readOnly } = useApp();
  const { route, actor } = useUi();
  const { commands } = useDemo();
  const { accounts, posts, brand, campaigns, templates, workspace } = useWorkspaceData(
    route.workspaceId
  );
  const formatDay = useDayFormat();
  const networks = [
    ...new Set(accounts.filter(isBusinessAccount).map((account) => account.network)),
  ] as Channel[];
  const [step, setStep] = useState(1);
  const [name, setName] = useState(campaign?.name ?? '');
  const [goal, setGoal] = useState<CampaignGoal>(campaign?.goal ?? 'product');
  const [ongoing, setOngoing] = useState(campaign ? !campaign.start : false);
  const [start, setStart] = useState(campaign?.start ?? 23);
  const [end, setEnd] = useState(campaign?.end ?? 29);
  const [channels, setChannels] = useState<Channel[]>(
    campaign?.channels ?? networks.filter((network) => network !== 'LinkedIn').slice(0, 2)
  );
  const [perWeek, setPerWeek] = useState(campaign?.perWeek ?? 4);
  const [brief, setBrief] = useState(campaign?.brief ?? '');
  const [materials, setMaterials] = useState<string[]>(campaign?.materials ?? []);
  const [picking, setPicking] = useState(false);
  const [template, setTemplate] = useState<string | undefined>(campaign?.template);
  const [audience, setAudience] = useState(campaign?.audience ?? '');
  const [focus, setFocus] = useState(campaign?.focus ?? '');
  const [languages, setLanguages] = useState<('pl' | 'en')[]>(
    campaign?.languages ?? (workspace.id === 'lipa' ? ['pl'] : ['pl'])
  );
  const [chosenAccounts, setChosenAccounts] = useState<string[]>(campaign?.accounts ?? []);
  const [plan, setPlan] = useState<PlanSlot[] | null>(null);
  const chosenTemplate = templates.find((item) => item.id === template);
  function chooseTemplate(id: string | undefined) {
    setTemplate(id);
    const nextTemplate = templates.find((item) => item.id === id);
    if (nextTemplate) {
      setGoal(nextTemplate.goal);
      if (!brief) setBrief(`${nextTemplate.description} `);
    }
  }
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);
  useEffect(() => {
    if (goal === 'always-on' && !campaign) setOngoing(true);
  }, [goal, campaign]);

  const channelAccounts = accounts.filter((account) => channels.includes(account.network));
  const pickedAccounts = chosenAccounts.length
    ? chosenAccounts.filter((id) => channelAccounts.some((account) => account.id === id))
    : channelAccounts.filter(isBusinessAccount).map((account) => account.id);
  const channelHealth = (channel: Channel) =>
    accounts.find((account) => account.network === channel && isBusinessAccount(account))?.health ??
    'Not connected';
  const nameTaken = campaigns.some(
    (otherCampaign) =>
      otherCampaign.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      otherCampaign.id !== campaign?.id
  );

  function findDateError() {
    if (end < start) return translate('The end date is before the start date.');
    if (start < TOMORROW && !campaign) return translate('Start from tomorrow or later.');
    return '';
  }
  const dateError = !ongoing && findDateError();

  const campaignLengthInDays = ongoing ? ALWAYS_ON_PLANNING_WINDOW_IN_DAYS : end - start + 1;
  const campaignDays = Array.from({ length: campaignLengthInDays }, (_, offset) => start + offset);
  const isSlotFree = (channel: Channel, candidateDay: number) =>
    candidateDay > DEMO_TODAY &&
    !posts.some(
      (post) =>
        post.channel === channel && post.day === candidateDay && holdsSlotStatus(post.status)
    );
  const countFreeSlotsAtOnePostPerChannelPerDay = () =>
    channels.reduce(
      (sum, channel) =>
        sum + campaignDays.filter((candidateDay) => isSlotFree(channel, candidateDay)).length,
      0
    );
  const freeSlots = ongoing
    ? campaignLengthInDays * channels.length
    : countFreeSlotsAtOnePostPerChannelPerDay();
  const wantedPosts = Math.ceil((perWeek * campaignLengthInDays) / DAYS_IN_A_WEEK);
  const tooFewFreeSlots = wantedPosts > freeSlots;
  const stepIsValid = [
    name.trim().length >= MINIMUM_NAME_LENGTH && !nameTaken,
    channels.length > 0 && !dateError && pickedAccounts.length > 0,
    brief.trim().length >= MINIMUM_BRIEF_LENGTH && languages.length > 0,
    true,
  ];

  function save(askIdeas: boolean) {
    const id = commands.saveCampaign(
      route.workspaceId,
      {
        name: name.trim(),
        goal,
        start: ongoing ? undefined : start,
        end: ongoing ? undefined : end,
        channels,
        perWeek,
        brief: brief.trim(),
        materials,
        audience: audience.trim() || undefined,
        focus: focus.trim() || undefined,
        languages,
        accounts: pickedAccounts,
        template,
        ...(plan && !campaign ? { plan } : {}),
      },
      actor,
      { id: campaign?.id, askIdeas: askIdeas && !plan }
    );
    onSaved(id);
  }

  const steps = ['Goal', 'Dates and channels', 'Brief', 'Check'];
  const isFirstStep = step === 1;
  const isLastStep = step === LAST_STEP;
  const chosenGoalLabel = goals.find(([goalId]) => goalId === goal)![1];

  function proposePlan() {
    setPlan(
      buildPlan(
        {
          goal,
          template,
          name: name.trim(),
          start: ongoing ? undefined : start,
          end: ongoing ? undefined : end,
          channels,
          perWeek,
          ideas: [],
          workspaceId: route.workspaceId as never,
        },
        posts
      )
    );
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer campaign-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="wizard-title">{translate(campaign ? 'Edit campaign' : 'New campaign')}</h2>
            <div className="muted">
              {translate('Step {n} of {total}: {name}', {
                n: step,
                total: WIZARD_STEP_COUNT,
                name: translate(steps[step - 1]),
              })}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <ol className="wizard-steps" aria-hidden="true">
          {steps.map((label, index) => {
            const stepNumber = index + 1;
            return (
              <li key={label} className={stepMarkerClass(stepNumber, step)}>
                {stepNumber < step ? <SafeIcon name="FiCheck" size={12} /> : stepNumber}
              </li>
            );
          })}
        </ol>

        {step === 1 && (
          <div className="wizard-body">
            <h3 tabIndex={-1} ref={headingRef}>
              {translate('Start from a template')}
            </h3>
            <div className="template-grid" role="radiogroup" aria-label={translate('Templates')}>
              {templates.map((item) => (
                <label
                  key={item.id}
                  className={`choice-card template-card ${template === item.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="template"
                    checked={template === item.id}
                    onChange={() => chooseTemplate(item.id)}
                  />
                  <span>
                    <strong>{translate(item.name)}</strong>
                    {item.by === 'Hakusana' && <span className="status neutral">Hakusana</span>}
                    <span className="muted">{translate(item.description)}</span>
                    <span className="template-example">
                      <em>{item.example.title}</em> — {item.example.caption}
                    </span>
                    <span className="muted small-print">
                      {translate('Needs')}:{' '}
                      {item.inputs.map((input) => translate(input)).join(', ')} ·{' '}
                      {translate('Formats')}:{' '}
                      {item.formats.map((format) => translate(format)).join(', ')}
                    </span>
                  </span>
                </label>
              ))}
              <label className={`choice-card template-card ${!template ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="template"
                  checked={!template}
                  onChange={() => chooseTemplate(undefined)}
                />
                <span>
                  <strong>{translate('Start blank')}</strong>
                  <span className="muted">{translate('Choose the goal yourself.')}</span>
                </span>
              </label>
            </div>
            <h3>{translate('What is this campaign for?')}</h3>
            <div className="goal-grid" role="radiogroup">
              {goals.map(([id, label, hint, icon]) => (
                <label key={id} className={`choice-card ${goal === id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    checked={goal === id}
                    onChange={() => setGoal(id)}
                  />
                  <SafeIcon name={icon as 'FiSun'} size={18} />
                  <span>
                    <strong>{translate(label)}</strong>
                    <span className="muted">{translate(hint)}</span>
                  </span>
                </label>
              ))}
            </div>
            <label className="wizard-field">
              {translate('Campaign name')}
              <input
                className="text-field"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={translate('For example: Hot chocolate launch')}
                aria-invalid={nameTaken}
              />
              {nameTaken && (
                <span className="field-error" role="alert">
                  {translate('A campaign with this name already exists.')}
                </span>
              )}
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-body">
            <h3 tabIndex={-1} ref={headingRef}>
              {translate('When and where?')}
            </h3>
            <label className="brand-check">
              <input
                type="checkbox"
                checked={ongoing}
                onChange={(event) => setOngoing(event.target.checked)}
              />{' '}
              {translate('No end date (always-on)')}
            </label>
            {!ongoing && (
              <div className="wizard-dates">
                <label className="wizard-field">
                  {translate('Start')}
                  <input
                    type="date"
                    className="text-field"
                    min={isoDateFromDemoDay(campaign ? Math.min(TOMORROW, start) : TOMORROW)}
                    max="2026-12-31"
                    value={isoDateFromDemoDay(start)}
                    onChange={(event) =>
                      event.target.value && setStart(demoDayFromIsoDate(event.target.value))
                    }
                  />
                </label>
                <label className="wizard-field">
                  {translate('End')}
                  <input
                    type="date"
                    className="text-field"
                    min={isoDateFromDemoDay(start)}
                    max="2026-12-31"
                    value={isoDateFromDemoDay(end)}
                    onChange={(event) =>
                      event.target.value && setEnd(demoDayFromIsoDate(event.target.value))
                    }
                  />
                </label>
              </div>
            )}
            {dateError && (
              <p className="field-error" role="alert">
                {dateError}
              </p>
            )}
            <fieldset className="wizard-channels">
              <legend>{translate('Channels')}</legend>
              {networks.map((channel) => {
                const health = channelHealth(channel);
                return (
                  <label
                    key={channel}
                    className={`choice-card ${channels.includes(channel) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={channels.includes(channel)}
                      onChange={() => setChannels(toggled(channels, channel))}
                    />
                    <SafeIcon name={channelIconName[channel] as 'FiInstagram'} size={16} />
                    <span>
                      <strong>{channel}</strong>
                      {health !== 'Connected' && (
                        <span className="field-warn">
                          {translate(
                            '{status}: AMY can plan, but nothing is published until it is reconnected.',
                            { status: translate(health) }
                          )}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            {channels.length === 0 && (
              <p className="field-error" role="alert">
                {translate('Choose at least one channel.')}
              </p>
            )}
            {channelAccounts.length > 0 && (
              <fieldset className="wizard-channels">
                <legend>{translate('Accounts')}</legend>
                {channelAccounts.map((account) => (
                  <label key={account.id} className="brand-check">
                    <input
                      type="checkbox"
                      checked={pickedAccounts.includes(account.id)}
                      onChange={() => setChosenAccounts(toggled(pickedAccounts, account.id))}
                    />
                    {account.name} · {translate(account.type)}
                    {account.health !== 'Connected' && (
                      <span className="field-warn"> · {translate(account.health)}</span>
                    )}
                  </label>
                ))}
                {pickedAccounts.length === 0 && (
                  <p className="field-error" role="alert">
                    {translate('Choose at least one account.')}
                  </p>
                )}
              </fieldset>
            )}
            <label className="wizard-field">
              {translate('Posts per week: {n}', { n: perWeek })}
              <input
                type="range"
                min={1}
                max={14}
                value={perWeek}
                onChange={(event) => setPerWeek(Number(event.target.value))}
              />
            </label>
            {!ongoing && !dateError && channels.length > 0 && (
              <div className={`notice-banner ${tooFewFreeSlots ? 'warn' : ''}`}>
                <SafeIcon name={tooFewFreeSlots ? 'FiAlertTriangle' : 'FiInfo'} />
                <span>
                  {tooFewFreeSlots
                    ? translate(
                        'About {w} posts are needed, but only {f} channel slots are free in these dates. AMY fills what fits; free slots or extend the dates for more.',
                        { w: wantedPosts, f: freeSlots }
                      )
                    : translate(
                        'About {w} posts fit into {f} free channel slots between {from} and {to}.',
                        { w: wantedPosts, f: freeSlots, from: formatDay(start), to: formatDay(end) }
                      )}
                </span>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="wizard-body">
            <h3 tabIndex={-1} ref={headingRef}>
              {translate('Tell AMY what matters')}
            </h3>
            <label className="wizard-field">
              {translate('Brief')}
              <textarea
                className="text-field"
                rows={5}
                value={brief}
                onChange={(event) => setBrief(event.target.value)}
                placeholder={translate(
                  'What is new, who it is for, what guests should do. For example: our new hot chocolate, for families on weekends, come to the window seats.'
                )}
              />
              <span className="muted small-print">
                {brief.trim().length < MINIMUM_BRIEF_LENGTH
                  ? translate('A sentence or two is enough.')
                  : translate('AMY also follows brand guide v{v}.', { v: brand.version })}
              </span>
            </label>
            {chosenTemplate && (
              <p className="muted small-print">
                {translate('Template “{name}” needs', { name: translate(chosenTemplate.name) })}:{' '}
                {chosenTemplate.inputs.map((input) => translate(input)).join(', ')}
              </p>
            )}
            <div className="wizard-dates">
              <label className="wizard-field">
                {translate('Audience')}
                <input
                  className="text-field"
                  value={audience}
                  onChange={(event) => setAudience(event.target.value)}
                  placeholder={translate('For example: families nearby, weekends')}
                />
              </label>
              <label className="wizard-field">
                {translate('Product focus')}
                <input
                  className="text-field"
                  value={focus}
                  onChange={(event) => setFocus(event.target.value)}
                  placeholder={translate('For example: hot chocolate, apple cake')}
                />
              </label>
            </div>
            <fieldset className="wizard-channels">
              <legend>{translate('Content languages')}</legend>
              <div className="filter-row">
                {(['pl', 'en'] as const).map((languageCode) => (
                  <label key={languageCode} className="brand-check compact">
                    <input
                      type="checkbox"
                      checked={languages.includes(languageCode)}
                      onChange={() => setLanguages(toggled(languages, languageCode))}
                    />{' '}
                    {translate(languageLabel(languageCode))}
                  </label>
                ))}
              </div>
              {languages.length === 0 && (
                <p className="field-error" role="alert">
                  {translate('Choose at least one language.')}
                </p>
              )}
            </fieldset>
            <h4>{translate('Materials for AMY')}</h4>
            <div className="chat-attachments">
              {materials.map((material) => (
                <span key={material} className="attachment-chip">
                  <SafeIcon name="FiImage" size={12} />
                  {material}
                  <button
                    aria-label={translate('Remove {word}', { word: material })}
                    onClick={() => setMaterials(materials.filter((other) => other !== material))}
                  >
                    <SafeIcon name="FiX" size={12} />
                  </button>
                </span>
              ))}
            </div>
            <button className="button small" onClick={() => setPicking(true)}>
              <SafeIcon name="FiPaperclip" size={14} />
              {translate(materials.length ? 'Add more material' : 'Add material')}
            </button>
            <p className="muted small-print">
              {translate('Optional. Without materials AMY uses your Material bank.')}
            </p>
          </div>
        )}

        {isLastStep && (
          <div className="wizard-body">
            <h3 tabIndex={-1} ref={headingRef}>
              {translate('Check and start')}
            </h3>
            <dl className="asset-facts wizard-summary">
              <div>
                <dt>{translate('Campaign name')}</dt>
                <dd>{name}</dd>
              </div>
              <div>
                <dt>{translate('Goal')}</dt>
                <dd>{translate(chosenGoalLabel)}</dd>
              </div>
              <div>
                <dt>{translate('Dates')}</dt>
                <dd>
                  {ongoing ? translate('Always-on') : `${formatDay(start)} – ${formatDay(end)}`}
                </dd>
              </div>
              <div>
                <dt>{translate('Channels')}</dt>
                <dd>{channels.join(', ')}</dd>
              </div>
              <div>
                <dt>{translate('Rhythm')}</dt>
                <dd>{translate('{n} posts a week', { n: perWeek })}</dd>
              </div>
              <div>
                <dt>{translate('Materials')}</dt>
                <dd>
                  {materials.length ? materials.join(', ') : translate('From the Material bank')}
                </dd>
              </div>
              <div>
                <dt>{translate('Accounts')}</dt>
                <dd>
                  {pickedAccounts
                    .map((id) => accounts.find((account) => account.id === id)?.name)
                    .join(', ')}
                </dd>
              </div>
              <div>
                <dt>{translate('Content languages')}</dt>
                <dd>
                  {languages
                    .map((languageCode) => translate(languageLabel(languageCode)))
                    .join(', ')}
                </dd>
              </div>
              {audience && (
                <div>
                  <dt>{translate('Audience')}</dt>
                  <dd>{audience}</dd>
                </div>
              )}
              {focus && (
                <div>
                  <dt>{translate('Product focus')}</dt>
                  <dd>{focus}</dd>
                </div>
              )}
            </dl>
            <p className="campaign-brief">{brief}</p>
            {!campaign && (
              <div className="wizard-plan">
                {!plan ? (
                  <button className="button" onClick={proposePlan}>
                    <SafeIcon name="FiList" />
                    {translate('Help me plan this')}
                  </button>
                ) : (
                  <>
                    <h4>{translate('Proposed plan: {n} posts', { n: plan.length })}</h4>
                    <PlanEditor
                      campaign={{
                        channels,
                        start: ongoing ? undefined : start,
                        end: ongoing ? undefined : end,
                      }}
                      plan={plan}
                      editable
                      onChange={setPlan}
                    />
                    <button className="link-button" onClick={() => setPlan(null)}>
                      {translate('Discard the plan')}
                    </button>
                  </>
                )}
                <p className="muted">
                  {translate(
                    plan
                      ? 'You review this plan on the campaign page before anything is drafted. Activation drafts two sample posts first.'
                      : 'AMY suggests ideas first. Nothing goes into the calendar until you accept an idea, and nothing is published before you approve it.'
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="picker-footer">
          <button className="button" onClick={() => (isFirstStep ? onClose() : setStep(step - 1))}>
            {isFirstStep ? (
              translate('Cancel')
            ) : (
              <>
                <SafeIcon name="FiArrowLeft" />
                {translate('Back')}
              </>
            )}
          </button>
          <div className="button-row">
            {step < LAST_STEP && (
              <button
                className="button primary"
                disabled={!stepIsValid[step - 1]}
                onClick={() => setStep(step + 1)}
              >
                {translate('Next')}
                <SafeIcon name="FiArrowRight" />
              </button>
            )}
            {isLastStep && campaign && (
              <button className="button primary" disabled={readOnly} onClick={() => save(false)}>
                <SafeIcon name="FiCheck" />
                {translate('Save changes')}
              </button>
            )}
            {isLastStep && !campaign && (
              <>
                <button className="button" disabled={readOnly} onClick={() => save(false)}>
                  {translate('Save as draft')}
                </button>
                <button
                  className="button primary"
                  disabled={readOnly || (!!plan && plan.length === 0)}
                  onClick={() => save(true)}
                >
                  <SafeIcon name={plan ? 'FiList' : 'FiZap'} />
                  {translate(plan ? 'Create with this plan' : 'Create and ask AMY for ideas')}
                </button>
              </>
            )}
          </div>
        </div>
      </section>
      {picking && (
        <MaterialPicker
          title="Choose material for this campaign"
          multiple
          onCancel={() => setPicking(false)}
          onPick={(items) => {
            setMaterials([...new Set([...materials, ...items.map((item) => item.name)])]);
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}
